import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBackOutline } from "react-icons/io5";
import { FaTrophy } from "react-icons/fa";
import { supabase } from "../ReusableComponents/supabaseClient";
import StatusBadge from "../ReusableComponents/StatusBadge";
import moment from "moment";
import "moment-timezone";
import { logError } from "../Utilities/errorLogger";
import { useAuth } from "../Utilities/AuthContext";

const AllTeamsView = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const { ownerData } = useAuth();
  const userId = ownerData?.userId || localStorage.getItem("userId");

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const { data, error } = await supabase
          .from("team")
          .select(`
            id, rank, paid,
            line_items:teamLineItem(count),
            pool (
              id, name, max_players, tournament_status,
              tournament_fixture ( name, start_date )
            )
          `)
          .eq("owner", userId)
          .order("pool(id)", { ascending: true });

        if (error) throw error;
        setTeams(data || []);
      } catch (error) {
        await logError({
          filePath: "Team/AllTeamsView.js",
          functionName: "fetchTeams",
          errorType: "Error",
          errorMessage: error.message,
          stackTrace: error.stack,
        });
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchTeams();
    else setLoading(false);
  }, [userId]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-white rounded-xl p-4 flex justify-between">
              <div className="space-y-2 flex-1"><div className="h-4 bg-gray-200 rounded w-2/3" /><div className="h-3 bg-gray-100 rounded w-1/3" /></div>
              <div className="h-6 w-16 bg-gray-200 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20 mb-24 md:mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary-light rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-green-200 hover:text-white transition-colors">
            <IoChevronBackOutline className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-white tracking-tight">My Teams</h1>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {teams.length === 0 ? (
        <div className="p-12 text-center">
          <p className="text-gray-400 text-lg">No teams yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {teams.map((team) => (
            <div
              key={team.id}
              onClick={() => navigate(`/pool/${team.pool.id}/team/${team.id}`)}
              className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-all cursor-pointer"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {team.pool.tournament_fixture?.name || team.pool.name}
                </p>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  <span>
                    {team.pool.tournament_fixture?.start_date
                      ? moment(team.pool.tournament_fixture.start_date).format("MMM D, YYYY")
                      : "No date"}
                  </span>
                  <span className={(team.line_items?.[0]?.count || 0) === team.pool.max_players ? "text-green-600" : "text-amber-600"}>
                    {team.line_items?.[0]?.count || 0}/{team.pool.max_players} players
                  </span>
                  {team.paid && <span className="text-green-600">Paid</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {team.rank === "1" && team.pool.tournament_status === "Official" && (
                  <FaTrophy className="w-4 h-4 text-yellow-500" />
                )}
                {team.rank && team.rank !== "MC" ? (
                  <span className="text-sm font-bold text-gray-900">#{team.rank}</span>
                ) : team.rank === "MC" ? (
                  <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded-full">Cut</span>
                ) : (
                  <StatusBadge status={team.pool.tournament_status || "Not Started"} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default AllTeamsView;
